
import { FaStar, FaChild, FaQuoteLeft, FaQuoteRight, FaHeart } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SectionHeading from '../ui/SectionHeading';
import Card from '../ui/Card';

export default function HostInfo() {
  return (
    <>
      <SectionHeading 
        title="Meet Your Host" 
        subtitle="Get to know the people behind Mikhael Homestay"
      />
      
      <motion.div 
        className="flex flex-col items-center gap-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <motion.div 
          className="relative flex-shrink-0 mx-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="w-64 h-64 relative rounded-2xl overflow-hidden shadow-lg border-4 border-white">
            <img
              src="/images/host.jpg"
              alt="Mikhael Homestay Host"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-3 -right-3 bg-blue-500 text-white rounded-full p-3 shadow-md">
            <FaStar className="text-xl" />
          </div>
        </motion.div>
        
        <div className="flex-1">
          <motion.div 
            className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-4"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="text-2xl font-semibold text-gray-900">Kaz</h3>
            <motion.div 
              className="flex items-center bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-sm"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <FaStar className="mr-1.5 text-yellow-300" />
              <span>4.9</span>
              <span className="mx-1.5 text-blue-200">·</span>
              <span>Superhost</span>
            </motion.div>
          </motion.div>
          
          <motion.p 
            className="text-gray-600 mb-6 text-center lg:text-left"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <span className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium">Hosting since 2023</span>
            <span className="mx-2 text-gray-400">•</span>
            <span className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium">100+ happy guests</span>
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="overflow-hidden border-0 shadow-lg">
              <div className="p-6 lg:p-8 relative">
                <motion.div 
                  className="absolute top-6 left-6 text-blue-400 text-4xl opacity-50"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 0.5, scale: 1, rotate: [0, 10, 0] }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <FaQuoteLeft />
                </motion.div>
                
                <motion.div 
                  className="absolute bottom-6 right-6 text-blue-400 text-4xl opacity-50"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 0.5, scale: 1, rotate: [0, -10, 0] }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <FaQuoteRight />
                </motion.div>
                
                <div className="prose max-w-none text-gray-700 relative z-10 px-4">
                  <motion.p 
                    className="font-medium text-lg leading-relaxed"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    Hi! We're Kaz and Anne, the name of this homestay is actually inspired by our beloved son.
                  </motion.p>
                  
                  <motion.p 
                    className="mt-4 leading-relaxed"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    This house was originally meant to be our family home after renovation. We put in our heart into designing and furnishing every corner. But after the sudden passing of my father, we never got to move in. Instead of leaving the house empty, we decided to turn it into a homestay, a way to keep it alive, clean, and filled with people.
                  </motion.p>
                  
                  <motion.p 
                    className="mt-4 leading-relaxed"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    We welcome you not just as guests, but like extended family. Feel free to make yourself at home, rest well, enjoy the space, and most importantly, help us take care of it too. It means a lot to us.
                  </motion.p>
                </div>
                
                <motion.div 
                  className="flex items-center mt-8 bg-gradient-to-r from-green-50 to-blue-50 p-5 rounded-xl border border-blue-100 shadow-sm"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  whileHover={{ y: -3, boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.1)" }}
                >
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-500 text-white shadow-md mr-4">
                    <FaChild className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800 mb-1">Family-Friendly</h4>
                    <p className="text-green-700 text-sm">Special amenities for children and families</p>
                  </div>
                </motion.div>
              </div>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
}
